import React from 'react';
import PaginatedTable from '../PaginatedTable';
import CsvButton from '../CsvButton';
import { fmtCount, fmtRatioPct, fmtPosition } from '../lib/format';

/** Export columns — raw payload values, not the rendered strings (see `lib/csv.js`). */
const CSV_COLUMNS = [
  { header: 'Appearance', get: (r) => r.search_appearance },
  { header: 'Showed up in searches', get: (r) => r.impressions },
  { header: 'Visits', get: (r) => r.clicks },
  { header: 'Click rate', get: (r) => r.ctr },
  { header: 'Position', get: (r) => r.position },
];

/**
 * Evidence for `search_appearance`.
 *
 * `facts`: { items: [{ search_appearance, clicks, impressions, ctr, position }],
 *            summary: { total_appearances, total_clicks, total_impressions } }
 *
 * `search_appearance` values are Google's raw enum strings (`REVIEW_SNIPPET`,
 * `MERCHANT_LISTINGS`) and are displayed EXACTLY as given — not title-cased, not
 * prettified. They are the names Google itself uses in Search Console and in its
 * documentation, so an owner searching for what one means needs the literal string.
 *
 * This panel is absent entirely on a property with no appearance data. Absent means no
 * enhanced results were recorded, not a failure — so there is no placeholder.
 */
const AppearanceList = ({ facts, sentence }) => {
  const items = Array.isArray(facts?.items) ? facts.items : [];

  const columns = [
    { key: 'search_appearance', label: 'Appearance', render: (r) => r.search_appearance },
    { key: 'impressions', label: 'Showed up in searches', align: 'right', render: (r) => fmtCount(r.impressions) },
    { key: 'clicks', label: 'Visits', align: 'right', render: (r) => fmtCount(r.clicks) },
    { key: 'ctr', label: 'Click rate', align: 'right', render: (r) => fmtRatioPct(r.ctr) },
    { key: 'position', label: 'Position', align: 'right', render: (r) => fmtPosition(r.position) },
  ];

  return (
    <div className="space-y-3">
      <PaginatedTable
        columns={columns}
        rows={items}
        // Index-suffixed: `search_appearance` is Google's enum and is normally unique, but
        // a duplicate or null value would collide into one React key and silently drop a
        // row from the table. The index costs nothing and removes the failure mode.
        rowKey={(r, i) => `${r.search_appearance ?? 'unknown'}-${i}`}
        label="appearances"
        ariaLabel={sentence}
      />
      <CsvButton panelKey="search_appearance" name="How your listing appeared" columns={CSV_COLUMNS} rows={items} />
    </div>
  );
};

export default AppearanceList;
